import { match } from 'ts-pattern';
import {
  getDisplayValueForMaybeUndefinedValue,
  percentage,
  sortValuesAlphabetically,
  sum,
} from '../../../../../../../utils';
import { formatPercentageNumber } from '../../../../../../../utils-display';
import { SortTypes } from '../../../../../../types';
import {
  TableData,
  TableViewHeader,
  TableViewRow,
} from '../../../../../tableview/types';
import {
  BarChartLayouts,
  ChartData,
  DataMapValue,
  ProcessTableDataOptions,
  SortedLabels,
} from '../common-chart-types';

export interface GetSortedLabelsData {
  labels: string[];
  sortedLabels?: SortedLabels;
  dataMap: Record<string, DataMapValue[]>;
}

export const getSortedLabels = (
  chartData: GetSortedLabelsData,
  selectedSortOrder: SortTypes
) => {
  const labelToSumValues = Object.fromEntries(
    Object.entries(chartData.dataMap).map(([label]) => [
      label,
      sum(chartData.dataMap[label].map((d) => d.value)),
    ])
  );
  const sortedLabels = match(selectedSortOrder)
    .with(SortTypes.ASC, () => {
      if (chartData.sortedLabels?.[SortTypes.ASC]) {
        return chartData.sortedLabels[SortTypes.ASC];
      } else {
        return sortValuesAlphabetically(
          chartData.labels.map((l) => ({ label: l }))
        ).map((l) => l.label);
      }
    })
    .with(SortTypes.DESC, () => {
      if (chartData.sortedLabels?.[SortTypes.DESC]) {
        return chartData.sortedLabels[SortTypes.DESC];
      } else {
        return sortValuesAlphabetically(
          chartData.labels.map((l) => ({ label: l })),
          false
        ).map((l) => l.label);
      }
    })
    .with(SortTypes.HIGH, () => {
      return [...chartData.labels].sort((a, b) => {
        const aSum = labelToSumValues[a];
        const bSum = labelToSumValues[b];
        if (aSum === bSum) {
          return 0;
        }
        return aSum > bSum ? -1 : 1;
      });
    })
    .with(SortTypes.LOW, () => {
      return [...chartData.labels].sort((a, b) => {
        const aSum = labelToSumValues[a];
        const bSum = labelToSumValues[b];
        if (aSum === bSum) {
          return 0;
        }
        return aSum < bSum ? -1 : 1;
      });
    })
    .exhaustive();
  return sortedLabels;
};

export const getBarChartTableDataFromChartData = (
  chartData: ChartData,
  options: ProcessTableDataOptions
): TableData => {
  const { layout, selectedSortOrder = SortTypes.ASC } = options;
  const { dataMap, labels, datasets } = chartData;
  const sortedLabels = selectedSortOrder
    ? getSortedLabels(chartData, selectedSortOrder)
    : labels;
  const headers: TableViewHeader[] = [
    {
      value: '',
    },
    ...datasets.map((dataset) => ({
      value: getDisplayValueForMaybeUndefinedValue(
        dataset.label ?? dataset.value
      ),
    })),
  ];
  const sumDataMapValues = Object.fromEntries(
    sortedLabels.map((label) => {
      return [label, sum(dataMap[label].map((data) => data.value))];
    })
  );
  const rows: TableViewRow[] = sortedLabels.map((label) => {
    return {
      key: label,
      title: getDisplayValueForMaybeUndefinedValue(label),
      cells: datasets.map((dataset) => {
        const total = sumDataMapValues[label];
        const value =
          dataMap[label].find((data) => data.datasetKey === dataset.value)
            ?.value ?? 0;
        const percentageValue = percentage(value, total);
        return {
          key: `${label}-${dataset.label}`,
          value:
            layout === BarChartLayouts.PERCENTAGE
              ? formatPercentageNumber(percentageValue)
              : (dataset.valueFormatter?.(value) ?? String(value)),
        };
      }),
    };
  });

  const formattedHeaders = headers.map((header) => ({
    value: getDisplayValueForMaybeUndefinedValue(header.value),
  }));
  return {
    headers: formattedHeaders,
    rows,
  };
};
